/* eslint-disable no-restricted-globals -- the one place allowed to touch Web Storage; that is the point of this file. */
import { Injectable } from '@angular/core';

import { StorageArea, StorageKey, StorageKeyPrefix } from './storage-keys';

/**
 * The app's only door onto `localStorage` / `sessionStorage`, enforced by an ESLint rule that
 * bans both identifiers everywhere else in `src/app/`.
 *
 * Web Storage fails in three ways that are easy to forget individually and impossible to forget
 * once: the accessor itself throws when a browser blocks site data, a write throws when the
 * quota is full, and a read returns whatever some earlier version of the app (or another tab,
 * or the user's devtools) left behind. Every method here absorbs all three — a read falls back
 * to the caller's value, a write is dropped. Nothing throws out of this service, because a
 * preference that cannot be remembered is not a reason to fail the operation the user asked for.
 *
 * Callers never name an area: it comes off the key, from the registry in `storage-keys.ts`.
 */
@Injectable({ providedIn: 'root' })
export class StorageService {
  /** The stored string, or `null` when the key is absent or storage is unavailable. */
  getString(key: StorageKey): string | null {
    return this.read(key.area, (store) => store.getItem(key.name), null);
  }

  setString(key: StorageKey, value: string): void {
    this.write(key.area, (store) => store.setItem(key.name, value));
  }

  /**
   * The stored value parsed as JSON, or `fallback` when the key is absent, holds malformed
   * JSON, or storage is unavailable. A stored `null` is a value and is returned as `null`.
   */
  getJson<T>(key: StorageKey, fallback: T): T {
    return this.read(
      key.area,
      (store) => {
        const raw = store.getItem(key.name);
        if (raw === null) return fallback;
        try {
          return JSON.parse(raw) as T;
        } catch {
          return fallback;
        }
      },
      fallback,
    );
  }

  /**
   * The stored value as a list of strings, dropping anything that is not one. Four call sites
   * store a set of ids this way, and each of them used to re-derive "is this really an array of
   * strings?" for itself — a question about the shape of stored data, which belongs here.
   */
  getStringArray(key: StorageKey): string[] {
    const value = this.getJson<unknown>(key, []);
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  }

  setJson(key: StorageKey, value: unknown): void {
    this.write(key.area, (store) => store.setItem(key.name, JSON.stringify(value)));
  }

  /** Whether the key is present, without caring what it holds. Used for flags. */
  has(key: StorageKey): boolean {
    return this.read(key.area, (store) => store.getItem(key.name) !== null, false);
  }

  remove(key: StorageKey): void {
    this.write(key.area, (store) => store.removeItem(key.name));
  }

  /** Drops every key in one namespace, in that namespace's area only. */
  removeByPrefix(prefix: StorageKeyPrefix): void {
    this.write(prefix.area, (store) => {
      const doomed: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const name = store.key(i);
        if (name?.startsWith(prefix.name)) {
          doomed.push(name);
        }
      }
      doomed.forEach((name) => store.removeItem(name));
    });
  }

  private read<T>(area: StorageArea, from: (store: Storage) => T, fallback: T): T {
    try {
      const store = this.store(area);
      return store ? from(store) : fallback;
    } catch {
      return fallback;
    }
  }

  private write(area: StorageArea, to: (store: Storage) => void): void {
    try {
      const store = this.store(area);
      if (store) to(store);
    } catch {
      // Quota exceeded, or site data blocked. Nothing to recover — the value is simply not kept.
    }
  }

  /**
   * Reading the global is itself the throwing step in a browser that blocks site data — hence
   * the call inside `read`/`write`'s `try` rather than a field captured at construction. A few
   * environments hand back `undefined` instead of throwing, which the callers also handle.
   */
  private store(area: StorageArea): Storage | undefined {
    return area === 'session' ? sessionStorage : localStorage;
  }
}
